import type { RouteTemplate, TripSegment } from '@der-maschinfuehrer/contracts';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { FormErrorSummary, TextField } from '../../components/form-primitives';
import { createRouteTemplateSourceSnapshot } from '../../domain/alpha-shift';
import type { AppStorage } from '../../storage/app-storage';

export function RouteTemplatesPanel({
  dataRevision,
  disabled = false,
  onApply,
  onCancelSave,
  sourceSegment,
  storage,
}: {
  readonly dataRevision: number;
  readonly disabled?: boolean;
  readonly onApply: (template: RouteTemplate) => Promise<boolean>;
  readonly onCancelSave: () => void;
  readonly sourceSegment: TripSegment | null;
  readonly storage: AppStorage;
}) {
  const [templates, setTemplates] = useState<readonly RouteTemplate[] | null>(null);
  const [name, setName] = useState('');
  const [favorite, setFavorite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const applying = useRef(false);
  const [errors, setErrors] = useState<readonly string[]>([]);

  useEffect(() => {
    let active = true;
    void storage.listRouteTemplates().then(
      (loaded) => {
        if (active) setTemplates(loaded);
      },
      (reason: unknown) => {
        if (active) setErrors([errorMessage(reason, 'Šablony se nepodařilo načíst.')]);
      },
    );
    return () => {
      active = false;
    };
  }, [dataRevision, storage]);

  const saveTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const source = sourceSegment;
    if (source === null || source.origin === null || source.destination === null) {
      setErrors(['Šablonu lze uložit jen z úseku se skutečným místem Odkud a Kam.']);
      return;
    }
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setErrors(['Zadej název šablony.']);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      const sourceSnapshot = createRouteTemplateSourceSnapshot(source);
      const saved = await storage.saveRouteTemplate({
        value: {
          route_template_id: crypto.randomUUID(),
          name: trimmedName,
          origin_location_id: source.origin.location_id,
          destination_location_id: source.destination.location_id,
          via_location_ids:
            source.route_snapshot?.via.map((location) => location.location_id) ?? [],
          preferred_route_variant_id: source.route_snapshot?.route_variant_id ?? null,
          default_train_or_service_number: source.train_or_service_number,
          default_work_type: source.work_type,
          default_operator: source.operator,
          default_vehicle_ids: source.vehicles.map((vehicle) => vehicle.vehicle_id),
          favorite,
          use_count: 0,
          source_snapshot: sourceSnapshot,
        },
        expected_revision: null,
      });
      setTemplates((current) => [...(current ?? []), saved]);
      setName('');
      setFavorite(false);
      setSaving(false);
      onCancelSave();
    } catch (reason) {
      setErrors([errorMessage(reason, 'Šablonu se nepodařilo uložit.')]);
      setSaving(false);
    }
  };

  const applyTemplate = async (template: RouteTemplate) => {
    if (disabled || applying.current) return;
    applying.current = true;
    setApplyingId(template.route_template_id);
    setErrors([]);
    try {
      if (template.source_snapshot === undefined) {
        throw new Error(
          'Tato starší šablona neobsahuje uloženou podobu trasy a nelze ji bezpečně použít. Nic nebylo předvyplněno.',
        );
      }
      if (!(await onApply(template))) {
        applying.current = false;
        setApplyingId(null);
      }
    } catch (reason) {
      applying.current = false;
      setErrors([errorMessage(reason, 'Šablonu se nepodařilo použít.')]);
      setApplyingId(null);
    }
  };

  return (
    <section className="route-templates-panel" aria-label="Uložené šablony úseků">
      <h3>Šablony</h3>
      <FormErrorSummary errors={errors} title="Šablonu nelze použít" />
      {templates === null ? <p role="status">Načítám šablony…</p> : null}
      {templates?.length === 0 ? <p>Zatím není uložená žádná šablona.</p> : null}
      {templates && templates.length > 0 ? (
        <ul className="template-list">
          {[...templates]
            .sort(
              (left, right) =>
                Number(right.favorite) - Number(left.favorite) ||
                right.use_count - left.use_count ||
                left.name.localeCompare(right.name, 'cs-CZ'),
            )
            .map((template) => (
              <li key={template.route_template_id}>
                <button
                  type="button"
                  className="secondary-action touch-target"
                  disabled={
                    disabled || applyingId !== null || template.source_snapshot === undefined
                  }
                  onClick={() => void applyTemplate(template)}
                >
                  {applyingId === template.route_template_id
                    ? 'Připravuji…'
                    : template.source_snapshot === undefined
                      ? `${template.name} · starší záznam nelze použít`
                      : template.name}
                  {template.favorite ? ' · oblíbená' : ''} · použito {template.use_count}×
                </button>
              </li>
            ))}
        </ul>
      ) : null}

      {sourceSegment ? (
        <form aria-label="Uložit úsek jako šablonu" onSubmit={(event) => void saveTemplate(event)}>
          <h4>Uložit vybraný úsek jako šablonu</h4>
          <TextField
            disabled={saving}
            label="Název šablony"
            maxLength={256}
            name="route-template-name"
            required
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <label className="checkbox-field">
            <input
              checked={favorite}
              disabled={saving}
              name="route-template-favorite"
              type="checkbox"
              onChange={(event) => setFavorite(event.currentTarget.checked)}
            />
            <span>Oblíbená šablona</span>
          </label>
          <div className="form-actions">
            <button type="submit" className="primary-action touch-target" disabled={saving}>
              {saving ? 'Ukládám šablonu…' : 'Uložit šablonu'}
            </button>
            <button
              type="button"
              className="secondary-action touch-target"
              disabled={saving}
              onClick={onCancelSave}
            >
              Zrušit
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error ? `${fallback} ${reason.message}` : fallback;
}
